/**
 * Key under which the login page finds the reason it was reached.
 *
 * sessionStorage rather than router state or a query parameter: the 401 handler signs the user
 * out by assigning `window.location.href`, which tears the SPA down and takes router state with
 * it, and the reason has no business being visible in — or forgeable through — the URL. Storage
 * is also per-tab, which matches the event: only the displaced tab should explain itself.
 */
const SESSION_EVICTED_KEY = 'dl.session-evicted';

/**
 * Fired when an eviction is recorded, so a login page that is ALREADY mounted learns about it.
 *
 * A sessionStorage write is invisible to React. Without this signal an eviction detected while
 * the login page is mounted — the API calls it makes itself can return `SESSION_EVICTED` — would
 * render no notice at all, and would leave behind a marker that the page had already cleared,
 * surfacing later as a phantom notice on an unrelated visit.
 */
export const SESSION_EVICTED_EVENT = 'dl:session-evicted';

/**
 * @description Records that this tab was signed out because the account was used to sign in
 * elsewhere, so the login page can explain the sign-out, and announces it to any listener that
 * is already mounted
 *
 * @SADtrace SAD-003-1.1
 *
 * @History
 * 08-18-2026 aleksandar dukleski: DLA-1469 created
 * 08-18-2026 aleksandar dukleski: DLA-1469 announce the mark so a mounted login page reacts
 *
 * @returns {void}
 */
export const markSessionEvicted = (): void => {
  try {
    sessionStorage.setItem(SESSION_EVICTED_KEY, '1');
  } catch {
    // Storage can be unavailable (private browsing, blocked site data). The explanation is a
    // courtesy; the sign-out redirect that follows it must happen regardless.
  }

  // Dispatched even when the write failed: the marker carries the notice across a reload, but
  // this event is what delivers it to a page that is not going to reload.
  window.dispatchEvent(new Event(SESSION_EVICTED_EVENT));
};

/**
 * @description Reports whether this tab was signed out by a sign-in elsewhere. Pure — reading it
 * repeatedly is safe, and clearing is a separate step
 *
 * @SADtrace SAD-003-1.1
 *
 * @History
 * 08-18-2026 aleksandar dukleski: DLA-1469 created
 *
 * @returns {boolean} True when the session was displaced by a sign-in on another device
 */
export const wasSessionEvicted = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_EVICTED_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * @description Discards the marker so the notice is shown once, for the sign-out that caused it,
 * and never again on a later visit to the login page
 *
 * @SADtrace SAD-003-1.1
 *
 * @History
 * 08-18-2026 aleksandar dukleski: DLA-1469 created
 *
 * @returns {void}
 */
export const clearSessionEvicted = (): void => {
  try {
    sessionStorage.removeItem(SESSION_EVICTED_KEY);
  } catch {
    // See markSessionEvicted: an unavailable store must not break the login page.
  }
};
